import React, { useState, useEffect } from 'react';
import { usePriceData } from '../../../hooks/usePriceData';
import { useForm, useWatch } from 'antd/es/form/Form';
import { trimValues } from '../../../utils/trimValues';
import { useNotificationContext } from '../../../contexts/NotificationContext';
import { useTranslation } from 'react-i18next';
import SessionSummary from '../../../components/price/SessionSummary';
import AggregatePresentations from '../../../components/price/AggregatePresentations';
import CollectionSessionInfoForm from '../../../components/price/CollectionSessionInfoForm';
import { referenceDataSyncService } from '../../../services/referenceDataSyncService';
import { localDataService } from '../../../services/localDataService';
import { Spin } from 'antd';
import BasicCard from '../../../components/basicCard/BasicCard';
import CitizenPriceAddPresentationForm from '../../../components/price/CitizenPriceAddPresentationForm';
import PrimaryButton from '../../../components/buttons/PrimaryButton';
import ConfirmModelComponent from '../../../components/model/ConfirmationModelComponent';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import RESOURCE_CONFIGS from '../../../constants/resourceConfig';
import { useIsOnlineContext } from '../../../contexts/IsOnlineContext';
import { locationType } from '../../../types/priceTypes';
import { getLocationCoordinates } from '../../../utils/getLocationCoordinates';
import { PriceRecordSourceEnum } from '../../../enums/PriceRecordSourceEnum';

const initialCollectionSessionInfo = {
  inspector: [],
  typeOfPosition: '',
  establishment: '',
  date: '',
};

export const CitizenPriceView: React.FC = () => {
  const { t } = useTranslation('price');
  const navigate = useNavigate();

  const [collectionSessionInfoDataForm] = useForm();
  const [addPresentationDataForm] = useForm();
  const [isConfirmButtonsDisable, setIsConfirmButtonsDisabled] = useState<boolean>(false);

  useEffect(() => {
    const fetchAndSetLocation = async () => {
      const location = await getLocationCoordinates();
      if (location) {
        const locationData = await referenceDataSyncService.getLocationByCoordinates(
          location.lat,
          location.long
        );
        if (locationData && locationData.length > 0) {
          const { district, city } = locationData[0];
          collectionSessionInfoDataForm.setFieldsValue({
            department: district,
            municipality: city,
          });
        }
      }
    };

    fetchAndSetLocation();
  }, []);

  const [collectionSessionInfo, setCollectionSessionInfo] = useState(initialCollectionSessionInfo);
  const [addedPresentations, setAddedPresentations] = useState<Record<string, unknown>[]>([]);
  const [addAggregatePresentations, setAddAggregatePresentations] = useState<
    Record<string, unknown>[]
  >([]);

  const [showAddPresentation, setShowAddPresentation] = useState<boolean>(false);

  const { successNotification, errorNotification } = useNotificationContext();

  const [resetPresentationSection, setResetPresentationSection] = useState(0);

  const dateVal = useWatch('date', collectionSessionInfoDataForm);
  const typeOfPositionVal = useWatch('typeOfPosition', collectionSessionInfoDataForm);
  const establishmentVal = useWatch('establishment', collectionSessionInfoDataForm);
  const establishmentOtherVal = useWatch('establishmentOther', collectionSessionInfoDataForm);

  const mainProductNameVal = useWatch('mainProductName', addPresentationDataForm);
  const productSearchVal = useWatch('productSearch', addPresentationDataForm);
  const [showSelectedSection, setShowSelectedSection] = useState(false);

  const [isConfirmationOpen, setIsConfirmationOpen] = useState<boolean>(false);

  const [isLoadingPrices, setIsLoadingPrices] = useState(false);
  const [disableFinishBtn, setDisableFinishBtn] = useState(false);

  const isMobile = useMediaQuery({
    query: `(max-width: ${RESOURCE_CONFIGS.RESPONSIVE_BREAKPOINTS.SM})`,
  });

  const { isOnline } = useIsOnlineContext();

  const { pricesLoading, createBulkPrice } = usePriceData({
    autoSync: true,
    sortBy: 'createTime',
    sortOrder: 'desc',
  });

  const deleteAggregatePresentation = (index: number) => {
    let productIdToDelete;
    setAddAggregatePresentations((prev) =>
      prev?.filter((product, i) => {
        if (i === index) {
          productIdToDelete = product?.productRefId;
        }
        return i !== index;
      })
    );
    setAddedPresentations((prev) => prev?.filter((_, i) => i !== index));
  };

  const handleCreatePrice = async () => {
    setIsConfirmButtonsDisabled(true);
    try {
      setIsLoadingPrices(true);

      const collectionSessionInfoFormData = collectionSessionInfoDataForm.getFieldsValue();
      setIsConfirmationOpen(false);
      setDisableFinishBtn(true);
      const trimmedValues = trimValues(collectionSessionInfoFormData);
      collectionSessionInfoDataForm.setFieldsValue(trimmedValues);
      await collectionSessionInfoDataForm.validateFields();

      const establishmentId = await referenceDataSyncService.getEstablishmentsId(
        trimmedValues?.typeOfPosition,
        trimmedValues?.establishment
      );
      const selectedLocationId = await referenceDataSyncService.getLocationId(
        trimmedValues?.department,
        trimmedValues?.municipality
      );

      const collectionSessionInfoObj = {
        ...trimmedValues,
        establishmentId: establishmentId[0]?.id,
        locationId: selectedLocationId,
      };
      setCollectionSessionInfo(collectionSessionInfoObj);

      /* location retrieval */
      let locationCoordinatesVal: locationType | null;
      const locationCoordinateResult = await getLocationCoordinates();
      if (locationCoordinateResult) {
        locationCoordinatesVal = locationCoordinateResult;
      } else {
        locationCoordinatesVal = null;
      }

      const collectionSessionInfoValues = collectionSessionInfoObj;
      const addPresentationValues = addedPresentations;
      const allPriceData = {
        ...collectionSessionInfoValues,
        productData: addPresentationValues,
      };

      const inspectionBody = {
        date: allPriceData?.date.valueOf(),
        locationId: allPriceData?.locationId,
        establishmentId: allPriceData?.establishmentId,
        establishmentOther: allPriceData?.establishmentOther,
        reportedTime: Date.now(),
        coordinates: locationCoordinatesVal,
      };

      const prices = allPriceData?.productData?.map((price: any) => ({
        ...price,
        reportedTime: Date.now(),
        coordinates: locationCoordinatesVal,
      }));

      try {
        const result = await localDataService.createInspectionWithPrices(inspectionBody, prices);

        if (result.inspection && result.prices.length > 0) {
          if (isOnline) {
            successNotification(t('priceRegisterSuccessOnline'), { duration: 10 });
          } else {
            successNotification(t('priceRegisterSuccessOffline'), { duration: 10 });
          }
        }

        setCollectionSessionInfo(initialCollectionSessionInfo);
        setAddedPresentations([]);
        setAddAggregatePresentations([]);
        collectionSessionInfoDataForm.resetFields();
        addPresentationDataForm.resetFields();
        navigate('/citizenPortal');
      } catch (error) {
        errorNotification(t('priceSubmissionFailed'));
      } finally {
        setIsLoadingPrices(false);
      }
    } catch (validationError) {
      setIsLoadingPrices(false);
    }
  };

  const handleCollectionSessionInfoNext = async () => {
    setShowAddPresentation(true);
  };

  const storeAddPresentationData = async () => {
    const addPresentationFormData = addPresentationDataForm.getFieldsValue();
    const photoVal = addPresentationDataForm.getFieldValue('photoOrInvoice');
    const productRefId = addPresentationDataForm.getFieldValue('productRefId');

    const photoOnlyDataUlrArr =
      photoVal?.map(({ image }: any) => {
        return image;
      }) || null;

    const trimmedValues = trimValues(addPresentationFormData);
    addPresentationDataForm.setFieldsValue(trimmedValues);

    const optimisedtrimmedValues = {
      photoOrInvoice: photoOnlyDataUlrArr,
      price: trimmedValues?.price,
      productRefId: trimmedValues?.productRefId,
      productRemarks: trimmedValues?.productRemarks,
      reportedTime: Date.now(),
      source: isMobile ? PriceRecordSourceEnum.MOBILE : PriceRecordSourceEnum.PC,
    };

    try {
      await addPresentationDataForm.validateFields();
      setAddedPresentations((prevProduct: any) => {
        const index = prevProduct.findIndex(
          (product: { productRefId: any }) => product.productRefId === trimmedValues?.productRefId
        );

        if (index !== -1) {
          const updatedProduct = [...prevProduct];
          updatedProduct[index] = { ...updatedProduct[index], ...optimisedtrimmedValues };
          return updatedProduct;
        } else {
          return [...prevProduct, optimisedtrimmedValues];
        }
      });

      setAddAggregatePresentations((prevProduct: any) => {
        const index = prevProduct.findIndex(
          (product: { productRefId: any }) => product.productRefId === trimmedValues?.productRefId
        );

        if (index !== -1) {
          const updatedProduct = [...prevProduct];
          updatedProduct[index] = { ...updatedProduct[index], ...trimmedValues };
          return updatedProduct;
        } else {
          return [...prevProduct, trimmedValues];
        }
      });

      addPresentationDataForm.resetFields();
      setResetPresentationSection((prev) => prev + 1);
      successNotification(t('presentationAddedToAggregate'));
    } catch (error) {
      console.error(error);
      errorNotification(t('failedToAddPresentationToAggregate'));
    }
  };

  const finishRegistration = async () => {
    await collectionSessionInfoDataForm.validateFields();
    setIsConfirmationOpen(true);
  };

  const handleCancel = () => {
    // Reset forms if needed
  };

  const addPresentationToList = () => {
    storeAddPresentationData();
  };

  const cancelConfirmation = () => {
    setIsConfirmationOpen(false);
  };

  if (pricesLoading) {
    return (
      <div className="accreditation-container">
        <div className="accreditation-counts">
          <div className="total-accreditation-container">
            <div className="total-accreditation total-back-bg">
              <div className="total-accreditation-text">
                <span>{t('loadingPrice')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <ConfirmModelComponent
        isOpen={isConfirmationOpen}
        onClickedCancel={cancelConfirmation}
        header={t('confirmPriceCreateTitle')}
        content={<p>{t('confirmPriceCreateMessage')}</p>}
        footerAction={{
          confimation: {
            label: t('confirm'),
            onClick: handleCreatePrice,
            isDisabled: isConfirmButtonsDisable,
          },
          cancel: {
            label: t('cancel'),
            onClick: cancelConfirmation,
          },
        }}
        styleClasses="z-100"
      />
      {isLoadingPrices ? (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-slate-100/60 transition-opacity">
          <Spin size={'large'} />
        </div>
      ) : null}
      <div className="mt-4 flex w-full justify-center">
        <div className="mt-4 text-center">
          <h1 className="mb-2 text-2xl font-bold">{t('priceMainHeading')}</h1>
          <p>{t('priceSubHeading')}</p>
        </div>
      </div>

      <div className="block w-full xl:hidden">
        <SessionSummary
          date={dateVal?.toString()}
          typeOfPosition={typeOfPositionVal?.toString()}
          establishment={establishmentVal?.toString()}
          establishmentOther={establishmentOtherVal?.toString()}
          products={addAggregatePresentations ?? []}
        />
      </div>

      <div className="my-4 flex min-h-screen w-full flex-col gap-4 xl:flex-row">
        <div className="ml-0 mt-4 flex-1 overflow-y-auto rounded-lg xl:ml-4">
          <BasicCard>
            <div className="relative p-4 md:p-6">
              <div className="flex items-center justify-center">
                <div className="m-4 w-full">
                  <div className="mb-4 space-y-2">
                    <h2 className="text-lg font-bold text-black md:text-xl">
                      {t('priceRecordHeading')}
                    </h2>
                    <p className="">{t('priceRecordSubHeading')}</p>
                  </div>

                  <CollectionSessionInfoForm
                    form={collectionSessionInfoDataForm}
                    priceRecord={collectionSessionInfo}
                    handleNext={handleCollectionSessionInfoNext}
                    handleCancel={handleCancel}
                    isModal={false}
                    isCitizen={true}
                    triggeredFromTask={false}
                    taskData={[]}
                  />

                  {showAddPresentation && (
                    <div className="mt-12">
                      <CitizenPriceAddPresentationForm
                        form={addPresentationDataForm}
                        addPresentationToList={addPresentationToList}
                        resetPresentationSection={resetPresentationSection}
                        onShowSelectedSectionChange={setShowSelectedSection}
                      />
                    </div>
                  )}

                  {addAggregatePresentations?.length > 0 &&
                    (!showSelectedSection || !mainProductNameVal || !productSearchVal) && (
                      <div className="mt-16 flex justify-end">
                        <PrimaryButton
                          handleOnClick={finishRegistration}
                          width={'100%'}
                          height="40px"
                          hideBgGradient={true}
                          disabled={disableFinishBtn}
                          className="flex items-center justify-center"
                        >
                          {t('finishRegistration')}
                        </PrimaryButton>
                      </div>
                    )}
                </div>
              </div>
            </div>
          </BasicCard>
        </div>

        <div className="hidden xl:block xl:w-1/4 xl:min-w-[280px]">
          <SessionSummary
            date={dateVal?.toString()}
            typeOfPosition={typeOfPositionVal?.toString()}
            establishment={establishmentVal?.toString()}
            establishmentOther={establishmentOtherVal?.toString()}
            products={addAggregatePresentations ?? []}
          />
          <AggregatePresentations
            products={addAggregatePresentations ?? []}
            finishRegistration={finishRegistration}
            deleteAggregatePresentation={deleteAggregatePresentation}
            productCount={addAggregatePresentations?.length}
            finishRegistrationClicked={0}
            isCitizen={true}
          />
        </div>
      </div>
      <div className="block w-full xl:hidden">
        <AggregatePresentations
          products={addAggregatePresentations ?? []}
          finishRegistration={finishRegistration}
          deleteAggregatePresentation={deleteAggregatePresentation}
          productCount={addAggregatePresentations?.length}
          finishRegistrationClicked={0}
          isCitizen={true}
        />
      </div>
    </>
  );
};

export default CitizenPriceView;
